import { useState, useEffect, useRef } from "react";
import { useSearch } from "@tanstack/react-router";
import {
  Search,
  Edit2,
  Trash2,
  User as UserIcon,
  Shield,
  Users,
  X,
  AlertTriangle,
  UserPlus,
  Play,
  Pause,
  Filter,
  Calendar,
  Folder,
  Settings2,
  CheckCircle,
} from "lucide-react";
import type { User, Team, Project } from "@/types/admin";
import adminService from "@/services/admin-service";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import UserMembershipsModal from "./UserMembershipsModal";

interface EditUserForm {
  user_name: string;
  global_role: string;
  team_id?: string;
  team_role?: string;
  status: string;
}

interface AddUserForm {
  user_email: string;
  user_name: string;
  global_role: string;
  team_id?: string;
  team_role?: string;
}

interface DeleteConfirmation {
  user: User;
  isOpen: boolean;
}

export default function UsersPage() {
  // Get query params from URL
  const searchParams = useSearch({ strict: false }) as {
    team_id?: string;
    project_id?: string;
  };
  const filterTeamId = searchParams.team_id;
  const filterProjectId = searchParams.project_id;

  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pagination states
  const [totalUsers, setTotalUsers] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [pageSize] = useState(50);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmation>({ user: {} as User, isOpen: false });

  // Form states
  const [editForm, setEditForm] = useState<EditUserForm>({
    user_name: "",
    global_role: "",
    team_id: "",
    team_role: "member",
    status: "active",
  });
  const [addForm, setAddForm] = useState<AddUserForm>({
    user_email: "",
    user_name: "",
    global_role: "member",
    team_id: "",
    team_role: "member",
  });
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Loading states
  const [submitting, setSubmitting] = useState(false);

  // Bulk assignment states
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignType, setBulkAssignType] = useState<"team" | "project">(
    "team",
  );
  const [bulkAssignTarget, setBulkAssignTarget] = useState<string>("");
  const [bulkAssignRole, setBulkAssignRole] = useState<string>("member");

  // Memberships modal state
  const [showMembershipsModal, setShowMembershipsModal] = useState(false);
  const [membershipsUser, setMembershipsUser] = useState<User | null>(null);

  // Success message state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Debounce ref for search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadUsers();
    loadTeams();
    loadProjects();
  }, [filterTeamId, filterProjectId, sortBy, sortOrder]);

  // Debounced search effect - triggers API call after user stops typing
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new debounced search - only trigger if we have filters/sort changes
    searchTimeoutRef.current = setTimeout(() => {
      loadUsers();
    }, 500);

    // Cleanup on unmount or when searchTerm changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]); // Only re-run when searchTerm changes

  const loadTeams = async () => {
    try {
      const teamsData = await adminService.listTeams();
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch (err) {
      console.error("Error loading teams:", err);
    }
  };

  const loadProjects = async () => {
    try {
      const projectsData = await adminService.listProjects();
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (err) {
      console.error("Error loading projects:", err);
    }
  };

  const loadUsers = async (cursor?: string, appendResults: boolean = false) => {
    try {
      if (!appendResults) {
        setLoading(true);
      }
      setError(null);

      // Build query params with pagination
      const params: any = {
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (filterTeamId) params.team_id = filterTeamId;
      if (filterProjectId) params.project_id = filterProjectId;
      if (searchTerm) params.search = searchTerm;
      if (cursor) params.cursor = cursor;

      const response = await adminService.listUsers(params);

      // Handle both old array format and new paginated format
      if (Array.isArray(response)) {
        setUsers(response);
        setTotalUsers(response.length);
        setHasMore(false);
      } else if (
        response &&
        typeof response === "object" &&
        "users" in response
      ) {
        const paginatedResponse = response as {
          users: User[];
          total: number;
          has_more: boolean;
          next_cursor?: string;
        };
        if (appendResults) {
          setUsers((prev) => [...prev, ...(paginatedResponse.users || [])]);
        } else {
          setUsers(paginatedResponse.users || []);
        }
        setTotalUsers(paginatedResponse.total || 0);
        setHasMore(paginatedResponse.has_more || false);
        setNextCursor(paginatedResponse.next_cursor);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load users");
      console.error("Error loading users:", err);
      if (!appendResults) {
        setUsers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (nextCursor && !loading) {
      loadUsers(nextCursor, true);
    }
  };

  // Search handler - just updates state, debounce handled by useEffect
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setNextCursor(undefined); // Reset pagination when searching
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || !editForm.user_name) {
      setError("User name is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await adminService.updateUser(editingUser.user_id, {
        user_name: editForm.user_name,
        global_role: editForm.global_role,
        status: editForm.status,
      });

      // Handle team assignment with proper team role
      if (editForm.team_id) {
        try {
          const teamRole = editForm.team_role || "member";
          await adminService.addTeamMember(
            editForm.team_id,
            editingUser.user_id,
            teamRole,
          );
        } catch (teamErr) {
          console.warn(
            "Team assignment may have failed, but user was updated:",
            teamErr,
          );
        }
      }

      setShowEditModal(false);
      setEditingUser(null);
      await loadUsers(); // Reload users list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update user");
      console.error("Error updating user:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.user_email || !addForm.user_name) {
      setError("Email and name are required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addForm.user_email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Validate team-level roles require team_id
    const teamLevelRoles = ["team_admin", "team_member"];
    if (teamLevelRoles.includes(addForm.global_role) && !addForm.team_id) {
      setError("Team selection is required for team-level roles");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Use invite endpoint which handles user creation
      // For team-level roles, team_id is required
      // For org-level roles, team_id should NOT be sent (backend rejects it)
      const orgLevelRoles = ["member", "org_admin", "super_admin"];
      const isOrgLevelRole = orgLevelRoles.includes(addForm.global_role);

      const response = await adminService.inviteUser({
        user_email: addForm.user_email,
        role: addForm.global_role || "member",
        // Only send team_id for team-level roles
        team_id: isOrgLevelRole ? undefined : addForm.team_id || undefined,
      });

      setShowAddModal(false);
      setAddForm({
        user_email: "",
        user_name: "",
        global_role: "member",
        team_id: "",
        team_role: "member",
      });

      // Show success message from API response
      const message =
        (response as any)?.detail ||
        (response as any)?.message ||
        `User ${addForm.user_email} has been invited successfully`;
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 8000); // Show for 8 seconds

      await loadUsers(); // Reload users list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to add user");
      console.error("Error adding user:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmation.user?.user_id) return;

    try {
      setSubmitting(true);
      setError(null);

      await adminService.deleteUser(deleteConfirmation.user.user_id);

      setDeleteConfirmation({ user: {} as User, isOpen: false });
      await loadUsers(); // Reload users list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete user");
      console.error("Error deleting user:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    // Get the first team assignment if available
    const firstTeam = user.teams?.[0];
    setEditForm({
      user_name: user.user_name,
      global_role: user.global_role,
      team_id: firstTeam?.team_id || user.team_id || "",
      team_role: firstTeam?.team_role || user.team_role || "member",
      status: user.status,
    });
    setShowEditModal(true);
  };

  const toggleUserStatus = async (user: User) => {
    try {
      setSubmitting(true);
      setError(null);

      const newStatus = user.status === "active" ? "suspended" : "active";
      await adminService.updateUser(user.user_id, {
        status: newStatus,
      });

      await loadUsers(); // Reload users list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update user status");
      console.error("Error updating user status:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteConfirmation = (user: User) => {
    setDeleteConfirmation({ user, isOpen: true });
  };

  const openMembershipsModal = (user: User) => {
    setMembershipsUser(user);
    setShowMembershipsModal(true);
  };

  // Bulk assignment handlers
  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  const selectAll = () => {
    setSelectedUserIds(new Set(filteredUsers.map((u) => u.user_id)));
  };

  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const handleBulkAssign = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const userIds = Array.from(selectedUserIds);

      if (bulkAssignType === "team") {
        const result = await adminService.bulkAddTeamMembers(
          bulkAssignTarget,
          userIds,
          bulkAssignRole,
        );

        if (result.succeeded > 0) {
          setError(null);
          alert(
            `Successfully assigned ${result.succeeded}/${result.total} users to team`,
          );
        }

        if (result.failed > 0) {
          const failedUsers = result.results.filter((r) => !r.success);
          console.warn("Failed assignments:", failedUsers);
          alert(`Warning: ${result.failed} assignments failed`);
        }
      } else {
        const result = await adminService.bulkAddProjectMembers(
          bulkAssignTarget,
          userIds,
          bulkAssignRole,
        );

        if (result.succeeded > 0) {
          setError(null);
          alert(
            `Successfully assigned ${result.succeeded}/${result.total} users to project`,
          );
        }

        if (result.failed > 0) {
          const failedUsers = result.results.filter((r) => !r.success);
          console.warn("Failed assignments:", failedUsers);
          alert(`Warning: ${result.failed} assignments failed`);
        }
      }

      // Clear selection and refresh
      clearSelection();
      setShowBulkAssignModal(false);
      setBulkAssignTarget("");
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Bulk assignment failed");
      console.error("Bulk assignment error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // No client-side filtering - server handles search via API
  const filteredUsers = users || [];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-4xl font-bold">User Management</h1>
            <p className="text-lg text-blue-100">
              Manage user roles and team assignments across your organization
            </p>
            {(filterTeamId || filterProjectId) && (
              <div className="mt-3 flex items-center gap-2">
                {filterTeamId && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm">
                    <Filter className="h-3 w-3" />
                    Filtered by Team
                  </span>
                )}
                {filterProjectId && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-sm font-medium text-white backdrop-blur-sm">
                    <Filter className="h-3 w-3" />
                    Filtered by Project
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="hidden sm:block">
            <div className="rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm">
              <p className="text-sm text-blue-100">Total Users</p>
              <p className="text-2xl font-bold">{filteredUsers.length}</p>
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Bulk Action Toolbar */}
      {selectedUserIds.size > 0 && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <CardBody>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {selectedUserIds.size} user(s) selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  Clear selection
                </button>
                {filteredUsers.length > selectedUserIds.size && (
                  <button
                    onClick={selectAll}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                  >
                    Select all ({filteredUsers.length})
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setBulkAssignType("team");
                    setBulkAssignRole("member"); // Reset to default team role
                    setBulkAssignTarget(""); // Reset target
                    setShowBulkAssignModal(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                >
                  <Users className="h-4 w-4" />
                  Assign to Team
                </button>

                <button
                  onClick={() => {
                    setBulkAssignType("project");
                    setBulkAssignRole("viewer"); // Reset to default project role
                    setBulkAssignTarget(""); // Reset target
                    setShowBulkAssignModal(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
                >
                  <Folder className="h-4 w-4" />
                  Assign to Project
                </button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Success Message Display */}
      {successMessage && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Success
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {successMessage}
                </p>
              </div>
              <button
                onClick={() => setSuccessMessage(null)}
                className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-3 pl-10 pr-4 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");
                  setSortBy(field);
                  setSortOrder(order as "asc" | "desc");
                  setNextCursor(undefined);
                }}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <option value="created_at-desc">Newest First</option>
                <option value="created_at-asc">Oldest First</option>
                <option value="user_name-asc">Name A-Z</option>
                <option value="user_name-desc">Name Z-A</option>
                <option value="user_email-asc">Email A-Z</option>
                <option value="user_email-desc">Email Z-A</option>
              </select>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:from-blue-600 hover:to-purple-700"
              >
                <UserPlus className="h-4 w-4" />
                Add User
              </button>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredUsers.length} users
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Users Table */}
      <Card hover shadow="lg">
        <div className="overflow-hidden">
          <div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={
                        selectedUserIds.size === filteredUsers.length &&
                        filteredUsers.length > 0
                      }
                      onChange={(e) =>
                        e.target.checked ? selectAll() : clearSelection()
                      }
                      className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Teams
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Projects
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Joined
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {filteredUsers.map((user) => (
                  <tr
                    key={user.user_id}
                    className="group transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.user_id)}
                        onChange={() => toggleUserSelection(user.user_id)}
                        className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-4">
                        {user.user_picture ? (
                          <img
                            src={user.user_picture}
                            alt={user.user_name}
                            className="h-12 w-12 rounded-xl object-cover shadow-lg"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 font-semibold text-white shadow-lg">
                            {user.user_name?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {user.user_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.user_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div
                          className={cn(
                            "rounded-lg p-1",
                            user.global_role === "super_admin" &&
                              "bg-gradient-to-r from-red-400 to-red-600",
                            user.global_role === "org_admin" &&
                              "bg-gradient-to-r from-purple-400 to-purple-600",
                            user.global_role === "team_admin" &&
                              "bg-gradient-to-r from-blue-400 to-blue-600",
                            user.global_role === "member" &&
                              "bg-gradient-to-r from-gray-400 to-gray-600",
                          )}
                        >
                          <Shield className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-sm font-medium capitalize text-gray-900 dark:text-white">
                          {user.global_role.replace("_", " ")}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex max-w-xs flex-col space-y-2">
                        {user.teams && user.teams.length > 0 ? (
                          user.teams.map((teamAssignment, idx) => {
                            const team = teams.find(
                              (t) => t.team_id === teamAssignment.team_id,
                            );
                            return (
                              <div
                                key={idx}
                                className="flex items-start space-x-2 text-sm"
                              >
                                <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                                <div className="flex min-w-0 flex-col">
                                  <span className="truncate font-medium text-gray-900 dark:text-white">
                                    {team?.team_name || teamAssignment.team_id}
                                  </span>
                                  <span className="text-xs capitalize text-gray-500 dark:text-gray-400">
                                    {teamAssignment.team_role.replace("_", " ")}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        ) : user.global_role === "super_admin" ||
                          user.global_role === "org_admin" ? (
                          <div className="flex items-center space-x-2 text-sm">
                            <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                            <span className="font-medium text-purple-600 dark:text-purple-400">
                              Global Access
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Not assigned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex max-w-xs flex-col space-y-2">
                        {user.projects && user.projects.length > 0 ? (
                          user.projects.map((projectAssignment, idx) => {
                            const project = projects.find(
                              (p) =>
                                p.project_id === projectAssignment.project_id,
                            );
                            return (
                              <div
                                key={idx}
                                className="flex items-start space-x-2 text-sm"
                              >
                                <Folder className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
                                <div className="flex min-w-0 flex-col">
                                  <span className="truncate font-medium text-gray-900 dark:text-white">
                                    {project?.project_name ||
                                      projectAssignment.project_id}
                                  </span>
                                  <span className="text-xs capitalize text-gray-500 dark:text-gray-400">
                                    {projectAssignment.project_role.replace(
                                      "_",
                                      " ",
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            No projects
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold",
                          user.status === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                        )}
                      >
                        <div
                          className={cn(
                            "mr-2 h-2 w-2 rounded-full",
                            user.status === "active"
                              ? "bg-green-500"
                              : "bg-red-500",
                          )}
                        />
                        {user.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          onClick={() => openMembershipsModal(user)}
                          className="rounded-lg p-2 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-900/20"
                          title="Manage team & project memberships"
                        >
                          <Settings2 className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => toggleUserStatus(user)}
                          className={cn(
                            "rounded-lg p-2 transition-colors",
                            user.status === "active"
                              ? "text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:hover:bg-orange-900/20"
                              : "text-green-600 hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-900/20",
                          )}
                          title={
                            user.status === "active"
                              ? "Suspend user"
                              : "Activate user"
                          }
                        >
                          {user.status === "active" ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </button>

                        <button
                          onClick={() => openEditModal(user)}
                          className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/20"
                          title="Edit user"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => openDeleteConfirmation(user)}
                          className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Pagination Footer - Always Visible */}
      <Card>
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {users.length}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {totalUsers}
                </span>{" "}
                users
              </div>
              {loading && (
                <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400"></div>
                  Loading...
                </div>
              )}
            </div>

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-600 hover:to-purple-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Loading..." : "Load More Users"}
              </button>
            )}

            {!hasMore && users.length > 0 && (
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                All users loaded
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Enhanced Empty State */}
      {filteredUsers.length === 0 && (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
                <UserIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No users found
              </h3>
              <p className="mx-auto max-w-sm text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? "Try adjusting your search term or filters to find what you're looking for."
                  : "Users will appear here when they register for your organization."}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Clear search
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-lg p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-blue-500 to-purple-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-purple-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <Edit2 className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowEditModal(false)}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Edit User
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Update user roles and team assignments
                  </p>
                </div>

                <form onSubmit={handleEditUser} className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      User Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={editForm.user_name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, user_name: e.target.value })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Global Role
                    </label>
                    <select
                      value={editForm.global_role}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          global_role: e.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <option value="member">Member (Org Level)</option>
                      <option value="org_admin">Org Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Global role applies at organization level. Use team
                      assignment below for team-specific roles.
                    </p>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Assign to Team
                    </label>
                    <select
                      value={editForm.team_id || ""}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          team_id: e.target.value,
                          team_role: "member",
                        })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <option value="">No team assignment</option>
                      {teams.map((team) => (
                        <option key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {editForm.team_id && (
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                        Team Role
                      </label>
                      <select
                        value={editForm.team_role || "member"}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            team_role: e.target.value,
                          })
                        }
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      >
                        <option value="member">Team Member</option>
                        <option value="team_admin">Team Admin</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Role within the selected team
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Status
                    </label>
                    <select
                      value={editForm.status}
                      onChange={(e) =>
                        setEditForm({ ...editForm, status: e.target.value })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>

                  {/* Role Description */}
                  <div className="rounded-xl border border-blue-200/30 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:border-blue-700/30 dark:from-blue-900/20 dark:to-indigo-900/20">
                    <div className="flex items-start space-x-3">
                      <Shield className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <div>
                        <h4 className="mb-1 text-sm font-semibold text-blue-900 dark:text-blue-200">
                          Role Permissions
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {editForm.global_role === "member" &&
                            "Basic member with read access to organization resources"}
                          {editForm.global_role === "org_admin" &&
                            "Can manage all teams and users within the organization"}
                          {editForm.global_role === "super_admin" &&
                            "Full administrator access to all organizations, teams and resources"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-600 hover:to-purple-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-lg p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-green-500 to-teal-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-green-600 to-teal-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <UserPlus className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setAddForm({
                      user_email: "",
                      user_name: "",
                      global_role: "member",
                      team_id: "",
                      team_role: "member",
                    });
                    setError(null);
                  }}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Add New User
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Invite a new user to the organization
                  </p>
                </div>

                {error && (
                  <div className="mb-4 flex items-start space-x-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {error}
                    </p>
                  </div>
                )}

                <form onSubmit={handleAddUser} className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="user@turing.com"
                      value={addForm.user_email}
                      onChange={(e) =>
                        setAddForm({ ...addForm, user_email: e.target.value })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="John Doe"
                      value={addForm.user_name}
                      onChange={(e) =>
                        setAddForm({ ...addForm, user_name: e.target.value })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Role
                    </label>
                    <select
                      value={addForm.global_role}
                      onChange={(e) => {
                        const newRole = e.target.value;
                        // Clear team_id if selecting org-level role
                        if (
                          ["member", "org_admin", "super_admin"].includes(
                            newRole,
                          )
                        ) {
                          setAddForm({
                            ...addForm,
                            global_role: newRole,
                            team_id: "",
                          });
                        } else {
                          setAddForm({ ...addForm, global_role: newRole });
                        }
                      }}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <optgroup label="Organization Level">
                        <option value="member">Member</option>
                        <option value="org_admin">Org Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </optgroup>
                      <optgroup label="Team Level (requires team selection)">
                        <option value="team_admin">Team Admin</option>
                        <option value="team_member">Team Member</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Team Selection - Required for team-level roles */}
                  {["team_admin", "team_member"].includes(
                    addForm.global_role,
                  ) && (
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                        Assign to Team <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={addForm.team_id || ""}
                        onChange={(e) =>
                          setAddForm({ ...addForm, team_id: e.target.value })
                        }
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      >
                        <option value="">Select a team</option>
                        {teams.map((team) => (
                          <option key={team.team_id} value={team.team_id}>
                            {team.team_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Optional Team Selection for org-level roles */}
                  {["member", "org_admin", "super_admin"].includes(
                    addForm.global_role,
                  ) && (
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                        Assign to Team (Optional)
                      </label>
                      <select
                        value={addForm.team_id || ""}
                        onChange={(e) =>
                          setAddForm({ ...addForm, team_id: e.target.value })
                        }
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      >
                        <option value="">No team assignment</option>
                        {teams.map((team) => (
                          <option key={team.team_id} value={team.team_id}>
                            {team.team_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Role Description */}
                  <div className="rounded-xl border border-green-200/30 bg-gradient-to-r from-green-50 to-teal-50 p-4 dark:border-green-700/30 dark:from-green-900/20 dark:to-teal-900/20">
                    <div className="flex items-start space-x-3">
                      <Shield className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-400" />
                      <div>
                        <h4 className="mb-1 text-sm font-semibold text-green-900 dark:text-green-200">
                          Role Permissions
                        </h4>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          {addForm.global_role === "member" &&
                            "Basic member with read access to organization resources"}
                          {addForm.global_role === "org_admin" &&
                            "Can manage all teams and users within the organization"}
                          {addForm.global_role === "team_admin" &&
                            "Can manage users and projects within assigned team"}
                          {addForm.global_role === "team_member" &&
                            "Basic team member with access to team projects"}
                          {addForm.global_role === "super_admin" &&
                            "Full administrator access to all organizations, teams and resources"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setAddForm({
                          user_email: "",
                          user_name: "",
                          global_role: "member",
                          team_id: "",
                        });
                        setError(null);
                      }}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        submitting ||
                        !addForm.user_email ||
                        !addForm.user_name ||
                        (["team_admin", "team_member"].includes(
                          addForm.global_role,
                        ) &&
                          !addForm.team_id)
                      }
                      className="flex-1 rounded-xl bg-gradient-to-r from-green-500 to-teal-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-green-600 hover:to-teal-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Adding..." : "Add User"}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Bulk Assignment Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div
                  className={cn(
                    "h-20 rounded-t-xl",
                    bulkAssignType === "team"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600"
                      : "bg-gradient-to-r from-purple-500 to-pink-600",
                  )}
                >
                  <div className="absolute -bottom-6 left-6">
                    <div
                      className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-lg ring-4 ring-white dark:ring-gray-800",
                        bulkAssignType === "team"
                          ? "bg-gradient-to-r from-blue-600 to-purple-700"
                          : "bg-gradient-to-r from-purple-600 to-pink-700",
                      )}
                    >
                      {bulkAssignType === "team" ? (
                        <Users className="h-6 w-6" />
                      ) : (
                        <Folder className="h-6 w-6" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => {
                    setShowBulkAssignModal(false);
                    setBulkAssignTarget("");
                  }}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Bulk Assign to{" "}
                    {bulkAssignType === "team" ? "Team" : "Project"}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Assign {selectedUserIds.size} selected user(s)
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select {bulkAssignType === "team" ? "Team" : "Project"}
                    </label>
                    <select
                      value={bulkAssignTarget}
                      onChange={(e) => setBulkAssignTarget(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">
                        -- Select{" "}
                        {bulkAssignType === "team" ? "Team" : "Project"} --
                      </option>
                      {bulkAssignType === "team"
                        ? teams.map((team) => (
                            <option key={team.team_id} value={team.team_id}>
                              {team.team_name}
                            </option>
                          ))
                        : projects.map((project) => (
                            <option
                              key={project.project_id}
                              value={project.project_id}
                            >
                              {project.project_name}
                            </option>
                          ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Role
                    </label>
                    <select
                      value={bulkAssignRole}
                      onChange={(e) => setBulkAssignRole(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      {bulkAssignType === "team" ? (
                        <>
                          <option value="member">Member</option>
                          <option value="team_admin">Team Admin</option>
                          <option value="viewer">Viewer</option>
                        </>
                      ) : (
                        <>
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="project_admin">Project Admin</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBulkAssignModal(false);
                      setBulkAssignTarget("");
                    }}
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkAssign}
                    disabled={submitting || !bulkAssignTarget}
                    className={cn(
                      "flex-1 rounded-xl px-4 py-3 text-sm font-semibold text-white transition-all",
                      bulkAssignType === "team"
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700",
                      (submitting || !bulkAssignTarget) &&
                        "cursor-not-allowed opacity-50",
                    )}
                  >
                    {submitting ? "Assigning..." : "Assign Users"}
                  </button>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-red-500 to-pink-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-pink-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() =>
                    setDeleteConfirmation({ user: {} as User, isOpen: false })
                  }
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Delete User
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    This action cannot be undone
                  </p>
                </div>

                <div className="mb-6">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      Are you sure you want to delete{" "}
                      <strong className="font-semibold">
                        {deleteConfirmation.user.user_name}
                      </strong>
                      ? This will permanently remove the user from all teams and
                      revoke all access permissions.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setDeleteConfirmation({ user: {} as User, isOpen: false })
                    }
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteUser}
                    disabled={submitting}
                    className="flex-1 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-pink-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? "Deleting..." : "Delete User"}
                  </button>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Memberships Modal */}
      {showMembershipsModal && membershipsUser && (
        <UserMembershipsModal
          user={membershipsUser}
          teams={teams}
          projects={projects}
          onClose={() => {
            setShowMembershipsModal(false);
            setMembershipsUser(null);
          }}
          onUpdate={() => {
            loadUsers();
          }}
        />
      )}
    </div>
  );
}
